import { useEffect, useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { Tab, Tabs } from "react-bootstrap";
import Offcanvas from "react-bootstrap/Offcanvas";
import "moment-timezone";

import MapAssetAndDevicesJS from "components/maps/MapAssetAndDevicesJS";
import MapManagerModal from "components/maps/MapManagerModal";
import ConditionalRender from "components/ConditionalRender";
import CoordinatesShowCopy from "components/coords/CoordinatesShowCopy";
import CoordinatesInputs from "components/coords/CoordinatesInputs";
import FormButton from "components/buttons/FormButton";
import TabGeneralManager from "../TabGeneralManager";
import DeviceLink from "../DeviceLink";
import AuthContext from "context/AuthContext";
import { subscribeEvent, unsubscribeEvent } from "core/eventsConnector";
import { SwalAlertClass } from "core/swalClass";
import axiApi from "ApiCaller";
import validateTabbedForm from "views/utils/validateTabbedForm";

const FormContainerBridge = ({
    show,
    setShow,
    btnDelete,
    setBtnDelete,
    item,
    items,
    setShowCanvasEdit,
    title,
    buttons,
    itemsShow,
    setItemsShow,
    setRefreshData,
    setPrev,
    add,
    paramsTypes,
}) => {
    const { user } = useContext(AuthContext);
    const canEdit = user.is_superuser || user.is_admin;

    const { t: _Tt } = useTranslation("tables");
    const { t: _Tf } = useTranslation("forms");

    const Alert = new SwalAlertClass({ confirmButtonText: "Aceptar" });

    const [onlyRead, setOnlyRead] = useState(btnDelete || !canEdit);
    const [activeTab, setActiveTab] = useState("general");
    const [formData, setFormData] = useState({ ...(item || {}) });

    const handleClose = () => setShow(false);

    const [isLoading, setIsLoading] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false);
    const [showMap, setShowMap] = useState(false);

    useEffect(() => {
        return () => {
            unsubscribeEvent("mapmodal:position:selected", handlePositionSelected);
        };
    }, []);

    useEffect(() => {
        if (activeTab === "location") {
            setShowMap(true);
        }
    }, [activeTab]);

    const handlePositionSelected = (e) => {
        const { lat, lng } = e.detail;
        setFormData((prev) => ({
            ...prev,
            lat: parseFloat(lat.toFixed(6)),
            lng: parseFloat(lng.toFixed(6)),
        }));
        unsubscribeEvent("mapmodal:position:selected", handlePositionSelected);
    };

    const handleChange = (event) => {
        let { name, value } = event.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleModalMap = () => {
        subscribeEvent("mapmodal:position:selected", handlePositionSelected);
        setShowMapModal(true);
    };

    const onSubmit = (e) => {
        setIsLoading(true);

        e.preventDefault();
        e.stopPropagation();
        let form = new FormData(e.target);
        console.log(form);
        const formValidations = validateTabbedForm(e.target);
        if (!formValidations.isValid) {
            Alert.warning(_Tf("labels.incomplete"), formValidations.errors.join("\n"));
            setIsLoading(false);
            return false;
        }

        if (title === "Añadir") {
            buttons.edit.disabled = true;
            buttons.delete.disabled = true;
        }
        axiApi
            .post("/api/assets/asset/save/", form)
            .then((resp) => {
                if (resp.status === 200) {
                    let itemsList = [];
                    if (formData.id) {
                        itemsList = [...itemsShow];
                        let itemidx = itemsList.findIndex((item) => item.uid === formData.idx);
                        itemsList[itemidx] = resp.data.item;
                    } else {
                        itemsList = [resp.data.item];
                    }
                    setItemsShow(itemsList);
                }
                setShow(false);
                setShowCanvasEdit(false);
                setIsLoading(false);
                Alert.success(_Tf("labels.success"), _Tf("success.title"));
            })
            .catch((error) => {
                // Error sending request suppressed for production
            });
    };

    const clickCancel = () => {
        setShow(false);
        setShowCanvasEdit();
        setBtnDelete(false);
    };

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" backdrop="static" style={{ width: "40%", fontSize: "15px" }}>
            <Offcanvas.Header className="preview-canvas">
                <div className="preview-icon">
                    <FontAwesomeIcon icon="bridge-water" />
                </div>

                <div className="preview-title">
                    <Offcanvas.Title>{btnDelete ? _Tt("containers.bridge") : title} Puente</Offcanvas.Title>
                </div>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form noValidate className="row g-3 mb-5 pb-5" onSubmit={onSubmit}>
                    <input type="hidden" id="id" name="id" value={formData.id} onChange={handleChange} />
                    <input type="hidden" id="idx" name="idx" value={formData.idx} onChange={handleChange} />
                    <input type="hidden" id="model_id" name="model_id" value={formData.model_id} onChange={handleChange} />
                    <Tabs
                        defaultActiveKey="general"
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        id="tab-form-container"
                        className="border-0 col-12">
                        <Tab eventKey="general" className="border-0" title={_Tt("containers.general")}>
                            <TabGeneralManager
                                item={item}
                                add={add}
                                btnDelete={btnDelete}
                                paramsTypes={paramsTypes}
                                setFormData={setFormData}
                                formData={formData}
                            />
                        </Tab>
                        <Tab eventKey="devices" className="border-0" title="Dispositivos">
                            <ConditionalRender condition={formData.devices && formData.devices.length > 0}>
                                {formData.devices.map((item_, index) => (
                                    <DeviceLink key={index} uuid={item_.uuid} />
                                ))}
                            </ConditionalRender>
                        </Tab>
                        <Tab eventKey="location" title={_Tt("containers.location")}>
                            <div style={{ marginLeft: "40px" }}>
                                <div className="d-flex col-12">
                                    <div className="d-flex align-items-center mt-3 col-8">
                                        <div className="col-3">
                                            <label htmlFor="address" className="col-form-label">
                                                {_Tt("containers.address")}{" "}
                                            </label>
                                        </div>
                                        <div className="col-10">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="address"
                                                name="address"
                                                defaultValue={formData.address}
                                                onChange={handleChange}
                                                readOnly={onlyRead}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-1"></div>
                                    <div className="d-flex align-items-center mt-3 col-4">
                                        <div className="col-2">
                                            <label htmlFor="number" className="col-form-label">
                                                Nº
                                            </label>
                                        </div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                className="form-control"
                                                id="number"
                                                name="number"
                                                defaultValue={formData.number}
                                                onChange={handleChange}
                                                readOnly={onlyRead}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex col-12">
                                    <div className="d-flex align-items-center mt-3 col-6">
                                        <div className="col-4">
                                            <label htmlFor="town" className="col-form-label">
                                                {_Tt("containers.town")}
                                            </label>
                                        </div>
                                        <div className="col-7">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="town"
                                                name="town"
                                                defaultValue={formData.municipality}
                                                onChange={handleChange}
                                                readOnly={onlyRead}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center mt-3 col-6">
                                        <div className="col-5">
                                            <label htmlFor="codpost" className="col-form-label">
                                                {_Tt("containers.cp")}
                                            </label>
                                        </div>
                                        <div className="col-5">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="codpost"
                                                name="codpost"
                                                maxLength="10"
                                                defaultValue={formData.codpost}
                                                onChange={handleChange}
                                                readOnly={onlyRead}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-3 align-items-center mt-3">
                                    <div className="col-2">
                                        <label htmlFor="province" className="col-form-label">
                                            {_Tt("containers.province")}
                                        </label>
                                    </div>
                                    <div className="col-9">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="province"
                                            name="province"
                                            defaultValue={formData.province}
                                            onChange={handleChange}
                                            readOnly={onlyRead}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="mt-4 col-11">
                                    <CoordinatesInputs
                                        item={formData}
                                        handleChange={handleChange}
                                        handleModalMap={handleModalMap}
                                    />
                                </div>

                                <div className="mt-4 col-11">
                                    <ConditionalRender condition={formData.devices && formData.devices.length > 0}>
                                        {formData.devices.map((item_, index) => (
                                            <CoordinatesShowCopy
                                                item={item_}
                                                key={index}
                                                formData={formData}
                                                setFormData={setFormData}
                                            />
                                        ))}
                                    </ConditionalRender>
                                    <div
                                        className="mt-4"
                                        style={{
                                            width: "100%",
                                            height: "200px",
                                        }}>
                                        <MapAssetAndDevicesJS
                                            asset={formData}
                                            devices={formData.devices}
                                            mapId="map-container-preview"
                                            height="200px"
                                            show={showMap}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Tab>
                    </Tabs>
                    <div className="p-5 text-end buttons-footer">
                        <button type="button" className="btn btn btn-secondary" onClick={clickCancel}>
                            {_Tt("containers.cancel")}
                        </button>

                        <FormButton type="submit" className="btn btn-primary ms-3" isProcessing={isLoading}>
                            Guardar
                        </FormButton>
                    </div>
                </form>
            </Offcanvas.Body>

            <MapManagerModal
                show={showMapModal}
                setShow={setShowMapModal}
                title={"Contenedor"}
                initialLat={formData.lat}
                initialLng={formData.lng}
            />
        </Offcanvas>
    );
};

export default FormContainerBridge;
